import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  GripVertical,
  FileText,
  Trash2,
  Copy,
  Edit,
  Eye,
  Download,
  ArrowLeft,
  Upload,
  MoreVertical,
  FileDown,
  CheckCircle,
  Clock,
  AlertCircle,
  Layers,
  Settings,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Lock,
  Search,
  Filter,
  Calendar,
  MapPin,
  Hash,
  GitBranch,
  ClipboardList,
  X
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SCHEDULE_ATTR_ROWS, SCHEDULE_BOUNDARIES, emptyScheduleItem } from '../lib/scheduleFields';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  under_review: { label: 'Under Review', color: 'bg-amber-100 text-amber-700', icon: Eye },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  active: { label: 'Active', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock }
};

// Sortable entry for standalone documents
const SortableEntry = ({ entry, onEdit, onDelete, onDuplicate, onViewFiles, ownershipInfo }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const fd = entry.form_data || {};
  const rawDate = fd.date || fd.deed_date || fd.dated || '';
  const surveyNo = fd.survey_number || fd.sy_no || fd.survey_no || '';
  
  // Format date to MM/DD/YYYY for display
  let dateVal = rawDate;
  if (rawDate && /^\d{4}-\d{2}-\d{2}/.test(rawDate)) {
    const [y, m, d] = rawDate.split('-');
    dateVal = `${m}/${d}/${y}`;
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border border-slate-200 rounded-lg flex items-center gap-3 group cursor-pointer ${
        isDragging ? 'shadow-lg' : 'hover:border-[#1e3a5f]/40 hover:shadow-md'
      } transition-all`}
      data-testid={`entry-${entry.id}`}
      onClick={(e) => {
        if (e.target.closest('[data-no-click]')) return;
        onEdit(entry);
      }}
    >
      <div className="flex items-center gap-3 p-4 flex-1 min-w-0">
        <button
          {...attributes}
          {...listeners}
          data-no-click
          className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`drag-handle-${entry.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical size={18} />
        </button>
        
        <div className="w-7 h-7 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
          {entry.render_order + 1}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-slate-900 text-sm">
              {entry.document_type_name}
              {entry.copy_suffix && (
                <span className="ml-1 text-xs font-normal text-indigo-500" data-testid={`entry-copy-${entry.id}`}>{entry.copy_suffix}</span>
              )}
            </h4>
            {entry.categoryName && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-slate-300 text-slate-500 bg-slate-50 font-medium" data-testid={`entry-category-${entry.id}`}>
                {entry.categoryName}
              </span>
            )}
            {ownershipInfo?.warning && (
              <span
                title={ownershipInfo.warning_message}
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-300 font-medium"
                data-testid={`ownership-warning-${entry.id}`}
              >
                <AlertCircle size={11} />
                Ownership mismatch
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {dateVal && (
              <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                <Calendar size={11} className="text-slate-400" />
                {dateVal}
              </span>
            )}
            {surveyNo && (
              <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                <Hash size={11} className="text-slate-400" />
                Sy. {surveyNo}
              </span>
            )}
            {(fd.district || fd.village) && (
              <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                <MapPin size={11} className="text-slate-400" />
                {fd.village || fd.district}
              </span>
            )}
          </div>
          {entry.remarks && (
            <p className="text-xs text-amber-600 mt-1">Remarks: {entry.remarks}</p>
          )}
          {ownershipInfo?.warning && (
            <div
              className="mt-2 text-[11px] bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 text-amber-800"
              data-testid={`ownership-warning-detail-${entry.id}`}
            >
              <p className="font-medium">⚠ {ownershipInfo.warning_message}</p>
              {ownershipInfo.expected_owners_before_display && (
                <p className="mt-0.5">
                  Previous owner(s):{' '}
                  <span className="font-medium">{ownershipInfo.expected_owners_before_display}</span>
                </p>
              )}
              {(ownershipInfo.owners_previous_raw || ownershipInfo.owners_raw) && (
                <p className="mt-0.5">
                  In this document:{' '}
                  <span className="font-medium">
                    {ownershipInfo.is_deed
                      ? `${ownershipInfo.owners_previous_raw || '—'} → ${ownershipInfo.owners_raw || '—'}`
                      : ownershipInfo.owners_raw || '—'}
                  </span>
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 pr-3" data-no-click>
        {entry.uploaded_files?.length > 0 && (
          <Button 
            variant="ghost" size="icon"
            onClick={(e) => { e.stopPropagation(); onViewFiles(entry); }}
            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-8 w-8"
          >
            <Upload size={15} />
          </Button>
        )}
        <Button
          variant="ghost" size="icon"
          onClick={(e) => { e.stopPropagation(); onEdit(entry); }}
          className="text-slate-400 hover:text-[#1e3a5f] hover:bg-blue-50 h-8 w-8"
          data-testid={`edit-btn-${entry.id}`}
        >
          <Edit size={15} />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-600" data-testid={`entry-menu-${entry.id}`} onClick={(e) => e.stopPropagation()}>
              <MoreVertical size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(entry); }}>
              <Edit size={14} className="mr-2" /> Edit Entry
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicate(entry); }} data-testid={`duplicate-btn-${entry.id}`}>
              <Copy size={14} className="mr-2" /> Duplicate Document
            </DropdownMenuItem>
            {entry.uploaded_files?.length > 0 && (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onViewFiles(entry); }}>
                <Eye size={14} className="mr-2" /> View Files ({entry.uploaded_files.length})
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(entry.id); }} className="text-red-600">
              <Trash2 size={14} className="mr-2" /> Delete Entry
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

const CaseDetailPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [entries, setEntries] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [selectedBulkTypes, setSelectedBulkTypes] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [viewingEntry, setViewingEntry] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [ownershipChain, setOwnershipChain] = useState({ chain: [], has_warnings: false, current_owners_display: '' });

  const canEdit = (() => {
    if (!user || !caseData) return false;
    if (isAdmin) return true;
    if (caseData.created_by === user.id) return true;
    return (caseData.assigned_users || []).includes(user.id);
  })();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const fetchData = async () => {
    try {
      const [caseRes, entriesRes, docTypesRes, ownershipRes] = await Promise.all([
        api.get(`/cases/${caseId}`),
        api.get(`/document-entries?case_id=${caseId}`),
        api.get('/document-types'),
        api.get(`/cases/${caseId}/ownership-chain`),
      ]);
      setCaseData(caseRes.data);
      setEntries(entriesRes.data);
      setDocumentTypes(docTypesRes.data);
      setSelectedStatus(caseRes.data.status);
      setOwnershipChain(ownershipRes.data || { chain: [], has_warnings: false });
    } catch (error) {
      toast.error('Failed to load case data');
      navigate('/cases');
    } finally {
      setLoading(false);
    }
  };

  const openScheduleDialog = () => {
    const existing = caseData?.schedule_property;
    setScheduleItems(existing && existing.length ? JSON.parse(JSON.stringify(existing)) : [emptyScheduleItem()]);
    setScheduleDialogOpen(true);
  };
  const updateScheduleItem = (idx, key, value) => {
    setScheduleItems((items) => items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  };
  const addScheduleItem = () => setScheduleItems((items) => [...items, emptyScheduleItem()]);
  const removeScheduleItem = (idx) => setScheduleItems((items) => items.filter((_, i) => i !== idx));
  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const cleaned = scheduleItems.filter((it) =>
        it.description || SCHEDULE_ATTR_ROWS.some(([k]) => it[k]) || SCHEDULE_BOUNDARIES.some(([k]) => it[k]));
      const res = await api.put(`/cases/${caseId}`, {
        case_name: caseData.case_name,
        client_name: caseData.client_name,
        schedule_property: cleaned,
      });
      setCaseData(res.data);
      toast.success('Schedule Property saved');
      setScheduleDialogOpen(false);
    } catch (e) {
      toast.error('Failed to save Schedule Property');
    } finally {
      setSavingSchedule(false);
    }
  };


  const refreshOwnershipChain = async () => {
    try {
      const res = await api.get(`/cases/${caseId}/ownership-chain`);
      setOwnershipChain(res.data || { chain: [], has_warnings: false });
    } catch {
      // non-fatal
    }
  };

  // Build simple flat list - no grouping, just attach category name for display
  const buildGroupedEntries = () => {
    return entries.map(entry => {
      const dt = documentTypes.find(d => d.id === entry.document_type_id);
      return { type: 'standalone', entry: { ...entry, categoryName: dt?.category || '' } };
    });
  };

  const getSortableIds = () => entries.map(e => e.id);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const ids = entries.map(e => e.id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newEntries = arrayMove([...entries], oldIndex, newIndex)
      .map((e, i) => ({ ...e, render_order: i }));
    setEntries(newEntries);
    
    try {
      await api.post('/document-entries/reorder', {
        entry_ids: newEntries.map(e => e.id)
      });
      toast.success('Order updated');
    } catch (error) {
      toast.error('Failed to update order');
      fetchData();
    }
  };

  const handleAddEntry = async () => {
    if (!selectedDocType) {
      toast.error('Please select a document type');
      return;
    }
    setAddDialogOpen(false);
    navigate(`/cases/${caseId}/entry?docType=${selectedDocType}`);
  };

  const handleBulkAdd = async () => {
    if (selectedBulkTypes.length === 0) {
      toast.error('Please select at least one document type');
      return;
    }

    try {
      for (const docTypeId of selectedBulkTypes) {
        const docType = documentTypes.find(dt => dt.id === docTypeId);
        const initialData = {};
        docType?.fields?.forEach(field => {
          if (field.type === 'survey_block') {
            initialData[field.name] = [{}];
          } else {
            initialData[field.name] = '';
          }
        });
        
        await api.post('/document-entries', {
          case_id: caseId,
          document_type_id: docTypeId,
          form_data: initialData,
          remarks: null
        });
      }
      
      toast.success(`Added ${selectedBulkTypes.length} document entries`);
      setBulkDialogOpen(false);
      setSelectedBulkTypes([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to add entries');
    }
  };

  const toggleBulkType = (docTypeId) => {
    setSelectedBulkTypes(prev => 
      prev.includes(docTypeId) 
        ? prev.filter(id => id !== docTypeId)
        : [...prev, docTypeId]
    );
  };

  const handleDeleteEntry = async (entryId) => {
    setDeleteTarget({ entryId });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { entryId } = deleteTarget;
    try {
      await api.delete(`/document-entries/${entryId}`);
      toast.success('Entry deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete entry');
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  const openDuplicateDialog = (entry) => {
    setDuplicateTarget(entry);
    setDuplicateDialogOpen(true);
  };

  const confirmDuplicate = async (copyFiles) => {
    if (!duplicateTarget) return;
    setDuplicating(true);
    try {
      await api.post(`/document-entries/${duplicateTarget.id}/duplicate?copy_files=${copyFiles ? 'true' : 'false'}`);
      toast.success('Document duplicated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate document');
    } finally {
      setDuplicating(false);
      setDuplicateDialogOpen(false);
      setDuplicateTarget(null);
    }
  };

  const handleStatusUpdate = async () => {
    setUpdatingStatus(true);
    try {
      await api.put(`/cases/${caseId}`, {
        ...caseData,
        status: selectedStatus
      });
      setCaseData(prev => ({ ...prev, status: selectedStatus }));
      toast.success('Status updated');
      setStatusDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleExport = async (format, withBlanks = true) => {
    setExporting(true);
    try {
      const response = await api.post('/ddr/generate', 
        { case_id: caseId, format, with_blanks: withBlanks },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = withBlanks ? '' : '_no_blanks';
      link.setAttribute('download', `DDR_${caseData.case_name.replace(/\s+/g, '_')}${suffix}.${format === 'pdf' ? 'pdf' : 'docx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${format.toUpperCase()} exported${withBlanks ? '' : ' (without blanks)'}`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPendingDocs = async (format) => {
    setExporting(true);
    try {
      const response = await api.get(`/cases/${caseId}/pending-documents/${format}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `PendingDocuments_${caseData.case_name.replace(/\s+/g, '_')}.${format}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Pending Documents ${format.toUpperCase()} exported`);
    } catch (error) {
      toast.error('Pending Documents export failed');
    } finally {
      setExporting(false);
    }
  };

  const openFilesDialog = (entry) => {
    setViewingEntry(entry);
    setFilesDialogOpen(true);
  };

  // Group document types by category for the dialogs
  const groupedDocTypes = documentTypes.reduce((acc, dt) => {
    if (!acc[dt.category]) acc[dt.category] = [];
    acc[dt.category].push(dt);
    return acc;
  }, {});

  const statusConfig = STATUS_CONFIG[caseData?.status] || STATUS_CONFIG.active;
  const StatusIcon = statusConfig.icon;
  const groupedEntries = buildGroupedEntries();
  const sortableIds = getSortableIds();
  const ownershipByEntry = React.useMemo(() => {
    const map = {};
    (ownershipChain.chain || []).forEach((c) => { if (c.entry_id) map[c.entry_id] = c; });
    return map;
  }, [ownershipChain]);
  const warningCount = (ownershipChain.chain || []).filter((c) => c.warning).length;

  // Get unique categories from entries for filtering
  const entryCategories = [...new Set(
    entries.map(e => {
      const dt = documentTypes.find(d => d.id === e.document_type_id);
      return dt?.category || 'Other';
    }).filter(Boolean)
  )];

  // Search & filter entries
  const filteredGroupedEntries = groupedEntries.filter(item => {
    const entry = item.entry;
    const q = searchQuery.toLowerCase();
    const fd = entry.form_data || {};
    const typeName = (entry.document_type_name || '').toLowerCase();
    const catName = (entry.categoryName || '').toLowerCase();
    const surveyNo = (fd.survey_number || fd.sy_no || fd.survey_no || '').toString().toLowerCase();
    
    let ownerNames = '';
    const ownerField = fd.owner_names || fd.owner_name || fd.in_name_of || fd.filed_by || '';
    if (Array.isArray(ownerField)) {
      ownerNames = ownerField.join(' ').toLowerCase();
    } else if (typeof ownerField === 'string') {
      ownerNames = ownerField.toLowerCase();
    }
    
    const matchesSearch = !searchQuery || typeName.includes(q) || catName.includes(q) || surveyNo.includes(q) || ownerNames.includes(q);
    const matchesCategory = filterCategory === 'all' || entry.categoryName === filterCategory;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-32 bg-slate-200 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="case-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/cases">
          <Button variant="ghost" size="icon">
            <ArrowLeft size={20} />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Playfair Display, serif' }}>
              {caseData?.case_name}
            </h1>
            <button 
              onClick={() => setStatusDialogOpen(true)}
              className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${statusConfig.color} cursor-pointer hover:opacity-80`}
              data-testid="status-badge"
            >
              <StatusIcon size={14} />
              {statusConfig.label}
            </button>
          </div>
          <p className="text-slate-500">Client: {caseData?.client_name}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={openScheduleDialog}
            data-testid="schedule-property-btn"
          >
            <ClipboardList size={18} className="mr-2" />
            Schedule Property
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/cases/${caseId}/ownership-flow`)}
            data-testid="ownership-flow-btn"
          >
            <GitBranch size={18} className="mr-2" />
            Ownership Flow
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/cases/${caseId}/preview`)}
            data-testid="preview-btn"
          >
            <Eye size={18} className="mr-2" />
            Preview DDR
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-[#1e3a5f] hover:bg-[#162c46]" disabled={exporting} data-testid="export-btn">
                <Download size={18} className="mr-2" />
                {exporting ? 'Exporting...' : 'Export'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64">
              <DropdownMenuItem onClick={() => handleExport('pdf', true)} data-testid="export-pdf-with-blanks">
                <FileDown size={16} className="mr-2" />
                Download PDF (With Blanks)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf', false)} data-testid="export-pdf-without-blanks">
                <FileDown size={16} className="mr-2" />
                Download PDF (Without Blanks)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('docx', true)} data-testid="export-docx-with-blanks">
                <FileText size={16} className="mr-2" />
                Download Word (With Blanks)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('docx', false)} data-testid="export-docx-without-blanks">
                <FileText size={16} className="mr-2" />
                Download Word (Without Blanks)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExportPendingDocs('pdf')} data-testid="export-pending-pdf">
                <FileDown size={16} className="mr-2" />
                Pending Documents (PDF)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportPendingDocs('docx')} data-testid="export-pending-docx">
                <FileText size={16} className="mr-2" />
                Pending Documents (Word)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Case Info Card */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-slate-500">Village Name</p>
              <p className="font-medium text-slate-900">{caseData?.case_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Client Name</p>
              <p className="font-medium text-slate-900">{caseData?.client_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Property Reference</p>
              <p className="font-medium text-slate-900">{caseData?.property_reference || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Created By</p>
              <p className="font-medium text-slate-900" data-testid="case-created-by">{caseData?.created_by_name || '-'}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-4">
            <div>
              <p className="text-sm text-slate-500">Status</p>
              <button 
                onClick={() => setStatusDialogOpen(true)}
                className={`mt-1 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 ${statusConfig.color} cursor-pointer hover:opacity-80`}
              >
                <StatusIcon size={16} />
                {statusConfig.label}
                <Settings size={14} className="ml-1 opacity-50" />
              </button>
            </div>
            {caseData?.case_location && Object.values(caseData.case_location).some(v => v) && (
              <div className="md:col-span-3">
                <p className="text-sm text-slate-500">Property Location</p>
                <p className="font-medium text-slate-900 mt-1" data-testid="case-location">
                  {[caseData.case_location.village, caseData.case_location.hobli, caseData.case_location.taluk, caseData.case_location.district].filter(Boolean).join(', ')}
                </p>
              </div>
            )}
          </div>
          
          <div className="mt-6 pt-6 border-t border-slate-100">
            <p className="text-sm font-medium text-slate-700 mb-3">Workflow Progress</p>
            <div className="flex items-center gap-2">
              {['draft', 'in_progress', 'under_review', 'completed'].map((status, idx) => {
                const config = STATUS_CONFIG[status];
                const isActive = caseData?.status === status;
                const isPast = ['draft', 'in_progress', 'under_review', 'completed'].indexOf(caseData?.status) > idx;
                
                return (
                  <React.Fragment key={status}>
                    <div 
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                        isActive ? config.color : isPast ? 'bg-green-50 text-green-600' : 'bg-slate-50 text-slate-400'
                      }`}
                    >
                      <config.icon size={16} />
                      <span className="text-xs font-medium">{config.label}</span>
                    </div>
                    {idx < 3 && (
                      <div className={`w-8 h-0.5 ${isPast || isActive ? 'bg-green-300' : 'bg-slate-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ownership Continuity Summary Banner */}
      {(ownershipChain.chain || []).length > 0 && (
        warningCount > 0 ? (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
            data-testid="ownership-banner-warning"
          >
            <AlertCircle className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-amber-900">
                Ownership continuity warning ({warningCount} {warningCount === 1 ? 'issue' : 'issues'})
              </p>
              <p className="text-amber-800 mt-1">
                Some documents show owners that don't match the chain. Ownership can change
                only through <b>Deeds</b>, <b>Mutation Register</b>, or <b>RR Pakka Book</b>.
                Affected rows below are highlighted.
              </p>
              {ownershipChain.current_owners_display && (
                <p className="text-amber-700 text-xs mt-1">
                  Current chain owner(s): <span className="font-medium">{ownershipChain.current_owners_display}</span>
                </p>
              )}
            </div>
          </div>
        ) : (
          <div
            className="rounded-md border border-green-200 bg-green-50 p-3 flex items-center gap-3"
            data-testid="ownership-banner-ok"
          >
            <CheckCircle className="text-green-600 flex-shrink-0" size={18} />
            <p className="text-sm text-green-800">
              Ownership chain is consistent.
              {ownershipChain.current_owners_display && (
                <> Current owner(s): <span className="font-medium">{ownershipChain.current_owners_display}</span>.</>
              )}
            </p>
          </div>
        )
      )}

      {/* Document Entries */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-xl text-[#1e3a5f]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Document Entries ({entries.length})
            </CardTitle>
            {canEdit ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setBulkDialogOpen(true)}
                  data-testid="bulk-add-btn"
                >
                  <Layers size={16} className="mr-1.5" />
                  Bulk Add
                </Button>
                <Button
                  size="sm"
                  onClick={() => setAddDialogOpen(true)}
                  className="bg-[#1e3a5f] hover:bg-[#162c46]"
                  data-testid="add-entry-btn"
                >
                  <Plus size={16} className="mr-1.5" />
                  Add Document
                </Button>
              </div>
            ) : (
              <span
                className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-600 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-full"
                data-testid="reference-only-badge"
              >
                <Eye size={12} /> Reference Only
              </span>
            )}
          </div>
          {/* Search & Filter Bar */}
          {entries.length > 0 && (
            <div className="flex items-center gap-3 mt-3">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, survey no, owner, category..."
                  className="pl-9 h-9 text-sm"
                  data-testid="entry-search"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-48 h-9 text-sm" data-testid="category-filter">
                  <Filter size={14} className="mr-1.5 text-slate-400" />
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {entryCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {entries.length === 0 ? (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 mb-4">No document entries yet.</p>
              {canEdit && (
                <div className="flex items-center justify-center gap-3">
                  <Button variant="outline" onClick={() => setBulkDialogOpen(true)}>
                    <Layers size={18} className="mr-2" />
                    Bulk Add Documents
                  </Button>
                  <Button onClick={() => setAddDialogOpen(true)} className="bg-[#1e3a5f] hover:bg-[#162c46]">
                    <Plus size={18} className="mr-2" />
                    Add Single Document
                  </Button>
                </div>
              )}
            </div>
          ) : filteredGroupedEntries.length === 0 ? (
            <div className="text-center py-8">
              <Search size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">No documents match your search.</p>
              <button 
                onClick={() => { setSearchQuery(''); setFilterCategory('all'); }}
                className="text-[#1e3a5f] text-sm mt-2 hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortableIds}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {filteredGroupedEntries.map((item) => (
                    <SortableEntry
                      key={item.entry.id}
                      entry={item.entry}
                      ownershipInfo={ownershipByEntry[item.entry.id]}
                      onEdit={(e) => navigate(`/cases/${caseId}/entry/${e.id}`)}
                      onDelete={handleDeleteEntry}
                      onDuplicate={openDuplicateDialog}
                      onViewFiles={openFilesDialog}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* Add Single Document Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Add Document Entry
            </DialogTitle>
            <DialogDescription>
              Select a document type to add to this case.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Document Type</Label>
            <Select value={selectedDocType} onValueChange={setSelectedDocType}>
              <SelectTrigger className="mt-2" data-testid="doc-type-select">
                <SelectValue placeholder="Select document type" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {Object.entries(groupedDocTypes).map(([category, types]) => (
                  <div key={category}>
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">
                      {category}
                    </div>
                    {types.map((dt) => (
                      <SelectItem key={dt.id} value={dt.id}>
                        {dt.name}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddEntry}
              className="bg-[#1e3a5f] hover:bg-[#162c46]"
              data-testid="continue-add-btn"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Add Documents Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Bulk Add Documents
            </DialogTitle>
            <DialogDescription>
              Select multiple document types to add at once.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500">
                {selectedBulkTypes.length} document(s) selected
              </p>
              <Button variant="outline" size="sm" onClick={() => setSelectedBulkTypes([])}>
                Clear Selection
              </Button>
            </div>
            
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {Object.entries(groupedDocTypes).map(([category, types]) => (
                <div key={category} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-slate-900">{category}</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const categoryIds = types.map(t => t.id);
                        const allSelected = categoryIds.every(id => selectedBulkTypes.includes(id));
                        if (allSelected) {
                          setSelectedBulkTypes(prev => prev.filter(id => !categoryIds.includes(id)));
                        } else {
                          setSelectedBulkTypes(prev => [...new Set([...prev, ...categoryIds])]);
                        }
                      }}
                    >
                      {types.every(t => selectedBulkTypes.includes(t.id)) ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {types.map((dt) => (
                      <label
                        key={dt.id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-50 ${
                          selectedBulkTypes.includes(dt.id) ? 'bg-blue-50 border border-blue-200' : ''
                        }`}
                      >
                        <Checkbox
                          checked={selectedBulkTypes.includes(dt.id)}
                          onCheckedChange={() => toggleBulkType(dt.id)}
                        />
                        <span className="text-sm">{dt.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkAdd}
              className="bg-[#1e3a5f] hover:bg-[#162c46]"
              disabled={selectedBulkTypes.length === 0}
              data-testid="bulk-add-submit-btn"
            >
              Add {selectedBulkTypes.length} Document(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Update Case Status
            </DialogTitle>
            <DialogDescription>
              Track the progress of this case through the workflow.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {['draft', 'in_progress', 'under_review', 'completed'].map((status) => {
              const config = STATUS_CONFIG[status];
              const isSelected = selectedStatus === status;
              
              return (
                <button
                  key={status}
                  onClick={() => setSelectedStatus(status)}
                  className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                    isSelected 
                      ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${config.color}`}>
                    <config.icon size={20} />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-slate-900">{config.label}</p>
                    <p className="text-xs text-slate-500">
                      {status === 'draft' && 'Initial stage, documents being collected'}
                      {status === 'in_progress' && 'Documents being reviewed and filled'}
                      {status === 'under_review' && 'Final review before completion'}
                      {status === 'completed' && 'DDR ready for delivery'}
                    </p>
                  </div>
                  {isSelected && (
                    <CheckCircle size={20} className="ml-auto text-[#1e3a5f]" />
                  )}
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleStatusUpdate}
              className="bg-[#1e3a5f] hover:bg-[#162c46]"
              disabled={updatingStatus}
            >
              {updatingStatus ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Files Preview Dialog */}
      <Dialog open={filesDialogOpen} onOpenChange={setFilesDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Uploaded Files - {viewingEntry?.document_type_name}
            </DialogTitle>
            <DialogDescription>
              View and download uploaded documents for this entry.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {viewingEntry?.uploaded_files?.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {viewingEntry.uploaded_files.map((filename) => {
                  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
                  
                  return (
                    <div key={filename} className="border border-slate-200 rounded-lg overflow-hidden">
                      {isImage ? (
                        <img 
                          src={`${BACKEND_URL}/api/uploads/${filename}`}
                          alt={filename}
                          className="w-full h-40 object-cover"
                        />
                      ) : (
                        <div className="w-full h-40 bg-slate-100 flex items-center justify-center">
                          <FileText size={48} className="text-slate-400" />
                        </div>
                      )}
                      <div className="p-3 bg-white">
                        <p className="text-sm text-slate-700 truncate">{filename}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <a 
                            href={`${BACKEND_URL}/api/uploads/${filename}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1"
                          >
                            <Button variant="outline" size="sm" className="w-full">
                              <Eye size={14} className="mr-1" />
                              View
                            </Button>
                          </a>
                          <a href={`${BACKEND_URL}/api/uploads/${filename}`} download>
                            <Button variant="outline" size="sm">
                              <Download size={14} />
                            </Button>
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">No files uploaded for this entry.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this document entry? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDeleteConfirmOpen(false); setDeleteTarget(null); }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              data-testid="confirm-delete-btn"
            >
              <Trash2 size={16} className="mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Document Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Duplicate Document</DialogTitle>
            <DialogDescription>
              A copy of <strong>{duplicateTarget?.document_type_name}</strong> will be created right below the original and labelled "(Copy)". All entered values are copied; the copy is kept out of the Ownership Flow until you enable it.
              <br /><br />
              Do you also want to duplicate the uploaded attachments?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button variant="ghost" onClick={() => { setDuplicateDialogOpen(false); setDuplicateTarget(null); }} disabled={duplicating} className="sm:mr-auto">
              Cancel
            </Button>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button variant="outline" onClick={() => confirmDuplicate(false)} disabled={duplicating} data-testid="duplicate-no-files-btn">
                No, values only
              </Button>
              <Button onClick={() => confirmDuplicate(true)} disabled={duplicating} data-testid="duplicate-with-files-btn" className="whitespace-nowrap">
                <Copy size={16} className="mr-2" />
                Yes, with attachments
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Schedule Property editor */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[86vh] overflow-y-auto" data-testid="schedule-property-dialog">
          <DialogHeader>
            <DialogTitle>Schedule Property</DialogTitle>
            <DialogDescription>
              These details appear on the first page of the DDR, before "I. Documents Furnished". Add one or more items.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {scheduleItems.map((item, idx) => (
              <div key={idx} className="border rounded-lg p-4 space-y-3" data-testid={`schedule-item-editor-${idx + 1}`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-800">ITEM NO. {idx + 1}</span>
                  {scheduleItems.length > 1 && (
                    <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700"
                      onClick={() => removeScheduleItem(idx)} data-testid={`remove-schedule-item-${idx + 1}`}>
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>
                <div className="space-y-1">
                  <Label>Schedule Property Description</Label>
                  <Textarea rows={3} value={item.description || ''}
                    onChange={(e) => updateScheduleItem(idx, 'description', e.target.value)}
                    placeholder="All that piece and parcel of land bearing Sy. No. ..."
                    data-testid={`schedule-desc-${idx + 1}`} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {SCHEDULE_BOUNDARIES.map(([key, label]) => (
                    <div key={key} className="space-y-1">
                      <Label>{label}</Label>
                      <Input value={item[key] || ''} onChange={(e) => updateScheduleItem(idx, key, e.target.value)}
                        data-testid={`schedule-${key}-${idx + 1}`} />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {SCHEDULE_ATTR_ROWS.map(([key, label]) => (
                    <div key={key} className="space-y-1">
                      <Label className="text-xs">{label}</Label>
                      <Input value={item[key] || ''} onChange={(e) => updateScheduleItem(idx, key, e.target.value)}
                        data-testid={`schedule-${key}-${idx + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addScheduleItem} data-testid="add-schedule-item-btn">
              <Plus size={16} className="mr-2" /> Add Another Item
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#1e3a5f] hover:bg-[#162c46]" onClick={saveSchedule} disabled={savingSchedule} data-testid="save-schedule-btn">
              {savingSchedule ? 'Saving…' : 'Save Schedule Property'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseDetailPage;
